// Data hook for Bank account history (register) page.
// Returns the TanStack Query compatible shape so backend swap is one file.
// Reads URL state flags: ?empty=1, ?loading=1, ?error=1.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MOCK_BANK_TRANSACTIONS,
  ALL_ACCOUNTS_LIST,
} from "../pages/Accounting/ChartOfAccounts/bank-history/mockData";
import type { BankAccountHistoryData } from "../pages/Accounting/ChartOfAccounts/bank-history/types";

interface UseBankAccountHistoryReturn {
  data: BankAccountHistoryData | undefined;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  lastRefreshedAt: Date | null;
}

const BANK_BALANCES: Record<string, number> = {
  a1: 12502.17,
  a10: 4210.00,
  a11: 320.00,
};

function sumNet(txs: Array<{ payment?: number; deposit?: number }>): number {
  return txs.reduce((acc, t) => acc + ((t.deposit ?? 0) - (t.payment ?? 0)), 0);
}

export function useBankAccountHistory(accountId: string | undefined): UseBankAccountHistoryReturn {
  const [searchParams] = useSearchParams();
  const forceEmpty = searchParams.get("empty") === "1";
  const forceLoading = searchParams.get("loading") === "1";
  const forceError = searchParams.get("error") === "1";

  const [loading, setLoading] = useState<boolean>(forceLoading);
  const [error, setError] = useState<Error | null>(
    forceError ? new Error("Failed to load bank account history") : null
  );
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(new Date());

  const data = useMemo<BankAccountHistoryData | undefined>(() => {
    if (loading || error || !accountId) return undefined;
    const account = ALL_ACCOUNTS_LIST.find((a) => a.id === accountId);
    if (!account) return undefined;

    const txs = forceEmpty ? [] : (MOCK_BANK_TRANSACTIONS[accountId] ?? []);
    const bankBalance = BANK_BALANCES[accountId] ?? 0;
    const endingBalance = sumNet(txs);

    return {
      account: {
        id: account.id,
        name: account.name,
        type: account.type,
        bankBalance,
      },
      transactions: txs,
      endingBalance,
      generatedAt: lastRefreshedAt ?? new Date(),
    };
  }, [accountId, forceEmpty, loading, error, lastRefreshedAt]);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    setTimeout(() => {
      setLoading(false);
      setLastRefreshedAt(new Date());
    }, 500);
  }, []);

  useEffect(() => {
    setLoading(forceLoading);
    setError(forceError ? new Error("Failed to load bank account history") : null);
  }, [forceLoading, forceError]);

  return { data, loading, error, refetch, lastRefreshedAt };
}